import { languageBase } from "@/lib/i18n/languages";

type Segmenter = {
  segment(input: string): Iterable<{ segment: string; isWordLike?: boolean }>;
};

function makeSegmenter(language: string, granularity: "word" | "sentence"): Segmenter | null {
  const SegmenterCtor = (Intl as typeof Intl & {
    Segmenter?: new (locales?: string | string[], options?: { granularity: "word" | "sentence" }) => Segmenter;
  }).Segmenter;
  if (!SegmenterCtor) return null;
  try {
    return new SegmenterCtor(language === "und" ? undefined : language, { granularity });
  } catch {
    return new SegmenterCtor(undefined, { granularity });
  }
}

/** Unicode-aware word boundaries, including scripts without spaces. */
export function segmentWords(text: string, language = "und"): string[] {
  if (!text) return [];
  const segmenter = makeSegmenter(language, "word");
  if (segmenter) {
    return Array.from(segmenter.segment(text))
      .filter((part) => part.isWordLike !== false && part.segment.trim())
      .map((part) => part.segment);
  }
  return text.split(/\s+/u).filter(Boolean);
}

/** Unicode-aware sentence boundaries with a conservative fallback. */
export function segmentSentences(text: string, language = "und"): string[] {
  if (!text) return [];
  const segmenter = makeSegmenter(language, "sentence");
  if (segmenter) {
    return Array.from(segmenter.segment(text))
      .map((part) => part.segment.trim())
      .filter((part) => part.length > 1);
  }
  return text
    .split(/(?<=[.!?。！？])[,\s]+/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 1);
}

export function countSegmentedWords(text: string, language = "und"): number {
  return segmentWords(text, language).length;
}

/** A conservative script hint used only when the writer has not selected a language. */
export function detectScriptLanguage(text: string): string | null {
  if (!text.trim()) return null;
  if (/[\u0600-\u06ff]/u.test(text)) return "ar";
  if (/[\u0900-\u097f]/u.test(text)) return "hi";
  if (/[\u0980-\u09ff]/u.test(text)) return "bn";
  if (/[\u0b80-\u0bff]/u.test(text)) return "ta";
  if (/[\u0c00-\u0c7f]/u.test(text)) return "te";
  if (/[\u3040-\u30ff]/u.test(text)) return "ja";
  if (/[\uac00-\ud7af]/u.test(text)) return "ko";
  if (/[\u4e00-\u9fff]/u.test(text)) return "zh-Hans";
  if (/[\u0e00-\u0e7f]/u.test(text)) return "th";
  if (/[\u0400-\u04ff]/u.test(text)) return "ru";
  return null;
}

export function segmentationLocale(language: string | null | undefined): string {
  return language && language !== "und" ? language : "und";
}

export function isCjkLanguage(language: string | null | undefined): boolean {
  return ["ja", "ko", "zh"].includes(languageBase(language));
}
